<template lang="pug">
    .example
        .example__container(
            :class="numberSizeClass"
            :style="exampleStyle"
        )
            span.example__operation.example__operation_type_root(v-if="example.operation === 'root'")
                | √
            span.example__part(:class="{'example__part_left': !playersNumber, 'example_border_overlined': example.operation === 'root'}")
                | {{ exampleNumber }}
            sup.example__operation.example__operation_type_exponentiation(v-if="example.operation === 'square'")
                | {{ 2 }}
        template(v-if="!dictation")
            .example__answer
                template(v-if="!playersNumber")
                    span.example__answer-header(v-if="!checkedWidthOfDevice")
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_enterTheAnswer') }}
                    span.example__answer-header(v-else)
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_answerForMulAndDiv') }}
                //TODO: add maxlength check for square and root operations based on answer size
                cs-input.example__input(
                    @input.prevent.native = "answer = handleDigitInput($event)"
                    @keyup.native.enter="checkAnswer(playersNumber > 1)"
                    ref="input"
                    :disabled="!startGame"
                    :correct="correct"
                    :wrong="!correct && !startGame"
                )
            cs-button(
                @click="checkAnswer(playersNumber > 1)",
                color="blue"
                :disabled="!startGame"
            )
                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
</template>

<script>
    import handleDigitInput from '../../../../utils/trainer/handleDigitInput';

    export default {
        props: {
            example: Object,
            checkMultiplayerAnswers: Boolean,
            dictation: Boolean,
            fractions: Boolean,
            fractionalPart: {
                type: [Number, String],
            },
            playersNumber: Number,
            colorForStudent: String,
            startGame: {
                type: Boolean,
                default: false,
            },
            correct: {
                type: Boolean,
                default: false,
            },
            isBattle: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                answer: '',
                isFocused: false,
                focusTimeout: null,
            };
        },
        mounted() {
            this.focusTimeout = setTimeout(() => {
                this.$refs.input?.$el.focus();
            }, 200);
        },
        watch: {
            example: {
                handler() {
                    this.$nextTick(() => {
                        this.$refs.input.$el.value = '';
                        this.answer = null;
                    });
                },
                deep: true,
            },
            checkMultiplayerAnswers(val) {
                if (val) {
                    this.checkAnswer(true);
                }
            },
        },
        updated() {
            if ((!this.isFocused && this.$refs.input) || this.startGame) {
                this.$refs.input.$el.focus();
                this.isFocused = true;
            }
        },
        computed: {
            exampleNumber() {
                return this.fractions ? this.example.number.toFixed(this.fractionalPart) : this.example.number;
            },
            checkedWidthOfDevice() {
                let width = window.innerWidth;
                return width < 768;
            },
            exampleStyle() {
                return {
                    color: this.playersNumber !== 0 && this.colorForStudent,
                };
            },
            numberSizeClass() {
                const length = this.exampleNumber.toString().length;
                if (length > 9 && this.example.operation === 'root') {
                    return 'example__container_size-large';
                }
                if (length > 5) {
                    return 'example__container_size-big';
                }
                return '';
            },
        },
        methods: {
            checkAnswer(isMultiplayer = false) {
                this.answer = this.answer && +this.answer.replace(',', '.');
                if (this.answer || this.answer === 0 || isMultiplayer) {
                    document.activeElement.blur();
                    this.isFocused = false;
                    this.$emit('answer', this.answer);
                }
            },
            handleDigitInput,
        },
        beforeDestroy() {
            clearTimeout(this.focusTimeout);
        },
    };
</script>

<style scoped lang="scss">
    .example {
        text-align: center;
    }

    .example__container {
        display: flex;
        justify-content: center;
        font-size: $font-size-40;
        font-weight: 500;
        flex-wrap: wrap;
        @include media($sm) {
            font-size: $font-size-60;
        }
        @include media($md) {
            font-size: $font-size-100;
        }
        @include media($lg) {
            font-size: $font-size-150;
        }
    }

    .example__container_size-big {
        font-size: $font-size-28;
        @include media($sm) {
            font-size: $font-size-42;
        }
        @include media($md) {
            font-size: $font-size-72;
        }
        @include media($lg) {
            font-size: $font-size-100;
        }
    }

    .example__container_size-large {
        font-size: $font-size-22;
        @include media($sm) {
            font-size: $font-size-32;
        }
        @include media($md) {
            font-size: $font-size-56;
        }
        @include media($lg) {
            font-size: $font-size-82;
        }
    }

    .example__answer {
        display: flex;
        flex-direction: column;
        align-items: center;
        @include stack-space($space-base);
    }

    .example__answer-header {
        @include stack-space($space-base);
        font-size: 22px;
        font-weight: 500;
        white-space: nowrap;
        @include media($md) {
            font-size: 32px;
        }
    }

    .example__input {
        max-width: 170px;
        margin-right: 10px;
        height: 80px;
        font-size: 22px !important;
        text-align: center;
        @include media($md) {
            max-width: 280px;
            font-size: 32px !important;
        }
    }

    .example_border_overlined {
        position: relative;
    }

    .example__container_size-large .example_border_overlined:after {
        border-top-width: 1px;
        top: 0;
        left: -1px;
        right: 0;
        @include media($sm) {
            border-top-width: 2px;
        }
        @include media($md) {
            border-top-width: 3px;
            top: 2px;
            left: -3px;
        }
        @include media($lg) {
            border-top-width: 5px;
            top: 3px;
            left: -5px;
        }
    }

    .example__container_size-big .example_border_overlined:after {
        border-top-width: 2px;
        top: 0;
        left: -1px;
        @include media($sm) {
            border-top-width: 2px;
            top: 1px;
            left: -2px;
        }
        @include media($md) {
            border-top-width: 4px;
            left: -3px;
        }
        @include media($lg) {
            border-top-width: 6px;
            top: 4px;
        }
    }

    .example_border_overlined:after {
        content: '';
        position: absolute;
        border-top: 2px solid $hsl-color-orange-56;
        top: 1px;
        left: 0;
        right: 0;
        @include media($sm) {
            border-top-width: 4px;
            top: 2px;
            left: -2px;
        }
        @include media($md) {
            border-top-width: 6px;
            top: 4px;
            left: -4px;
        }
        @include media($lg) {
            border-top-width: 8px;
            top: 4px;
            left: -9px;
        }
    }

    .example__part_left {
        color: $hsl-color-blue-56;
    }

    .example__operation {
        color: $hsl-color-orange-56;
    }

    .example__operation_type_exponentiation {
        font-size: 50%;
        top: 0.2em;
    }

    .example__operation_type_root {
        transform: scaleY(1.2);
    }

    $players-amount-sizes: 12, 6, 4, 3;

    .multiplayer {
        .example {
            .cs-button {
                font-weight: 500;
            }
        }
    }

    @mixin set_overline_size($border-top, $top, $left) {
        .example_border_overlined:after {
            border-top-width: $border-top;
            top: $top;
            left: $left;
        }
    }

    @each $col-size in $players-amount-sizes {
        .multiplayer__item--is-#{$col-size} .example__container {
            @if $col-size == 6 {
                @include set_overline_size(3px, 0, -2px);
                font-size: 40px;
                @include media($xl) {
                    @include set_overline_size(3px, 0, -3px);
                    font-size: 50px;
                }
                @include media($xxl) {
                    @include set_overline_size(4px, 1px, -2px);
                    font-size: 70px;
                }
            }
            @if $col-size == 4 {
                @include set_overline_size(2px, 1px, -1px);
                font-size: 30px;
                @include media($xl) {
                    font-size: 35px;
                }
                @include media($xxl) {
                    @include set_overline_size(3px, 0, -3px);
                    font-size: 50px;
                }
            }
            @if $col-size == 3 {
                @include set_overline_size(2px, 1px, -1px);
                font-size: 30px;
                @include media($xxl) {
                    @include set_overline_size(3px, 0, -2px);
                    font-size: 40px;
                }
            }
        }

        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .example__container {
            @if $col-size == 6 {
                @include set_overline_size(2px, 1px, -1px);
                font-size: 30px;
                @include media($xl) {
                    @include set_overline_size(3px, 0, -3px);
                    font-size: 50px;
                }
            }
            @if $col-size == 4 {
                @include set_overline_size(2px, 1px, -1px);
                font-size: 30px;
                @include media($xl) {
                    font-size: 35px;
                }
                @include media($xxl) {
                    @include set_overline_size(3px, 0, -3px);
                    font-size: 50px;
                }
            }
            @if $col-size == 3 {
                @include set_overline_size(1px, 0, -1px);
                font-size: 22px;
                @include media($xl) {
                    @include set_overline_size(2px, 1px, -1px);
                    font-size: 30px;
                }
                @include media($xxl) {
                    @include set_overline_size(3px, 0, -2px);
                    font-size: 40px;
                }
            }
        }

        .multiplayer__item--is-#{$col-size} .example__input {
            @if $col-size == 6 {
                max-width: 250px;
                max-height: 70px;
                @include media($xl) {
                    max-height: 80px;
                }
            } @else if $col-size == 4 {
                max-width: 180px;
                max-height: 42px;
                @include media($xl) {
                    max-width: 250px;
                    max-height: 60px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 80px;
                }
            } @else if $col-size == 3 {
                max-width: 150px;
                max-height: 40px;
                @include media($xl) {
                    max-width: 250px;
                    max-height: 50px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 80px;
                }
            }
        }

        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .example__input {
            @if $col-size == 6 {
                max-width: 150px;
                max-height: 40px;
                @include media($xl) {
                    max-width: 250px;
                    max-height: 50px;
                }
            } @else if $col-size == 4 {
                max-width: 150px;
                max-height: 40px;
                @include media($xl) {
                    max-width: 250px;
                    max-height: 50px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 60px;
                }
            } @else if $col-size == 3 {
                max-width: 110px;
                max-height: 32px;
                @include media($xl) {
                    max-width: 180px;
                    max-height: 42px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 60px;
                }
            }
        }
    }
</style>
